import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { userAPI, entityAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Edit2, X, Check, Phone, MapPin, Wallet, ChefHat, Store, Award, Clock } from 'lucide-react';
import SellerLayout from '../../components/layouts/SellerLayout';

const SELLER_BG = "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1200";
const VENDOR_BG = "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200";

const GENDER_LABELS = {
  male: 'Male',
  female: 'Female',
  other: 'Other'
};

export default function SellerProfile() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [entityName, setEntityName] = useState('');
  const [profileData, setProfileData] = useState(null);
  
  const isDealer = user?.role === 'dealer';
  const basePath = isDealer ? '/dealer' : '/seller';
  const bgImage = isDealer ? VENDOR_BG : SELLER_BG;
  
  const [formData, setFormData] = useState({
    age: '',
    gender: '',
    address: '',
    photo_url: '',
    upi_code: '',
    specialization: '',
    code_name: '',
    cooking_experience: ''
  });

  useEffect(() => {
    loadProfile();
    loadEntityName();
  }, []);

  const loadEntityName = async () => {
    try {
      const response = await entityAPI.getAll();
      const entity = response.data.find(e => e.id === user.entity_id);
      if (entity) {
        setEntityName(`${entity.name}, ${entity.city}`);
      }
    } catch (error) {
      console.error('Failed to load entity');
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await userAPI.getProfile(user.id);
      const profile = response.data.profile || {};
      setProfileData(profile);
      setFormData({
        age: profile.age || '',
        gender: profile.gender || '',
        address: profile.address || '',
        photo_url: profile.photo_url || '',
        upi_code: profile.upi_code || '',
        specialization: profile.specialization || '',
        code_name: profile.code_name || '',
        cooking_experience: profile.cooking_experience || ''
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const profileDataToSave = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null
      };
      
      const response = await userAPI.updateProfile(user.id, profileDataToSave);
      setProfileData(response.data.profile);
      updateUser({ profile: response.data.profile });
      setEditMode(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setFormData({
      age: profileData?.age || '',
      gender: profileData?.gender || '',
      address: profileData?.address || '',
      photo_url: profileData?.photo_url || '',
      upi_code: profileData?.upi_code || '',
      specialization: profileData?.specialization || '',
      code_name: profileData?.code_name || '',
      cooking_experience: profileData?.cooking_experience || ''
    });
    setEditMode(false);
  };

  if (loading) {
    return (
      <SellerLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SellerLayout>
    );
  }

  // Profile View Mode
  if (!editMode) {
    return (
      <SellerLayout>
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Header with Background */}
          <div className="relative rounded-3xl overflow-hidden">
            <div className="h-48 bg-cover bg-center" style={{ backgroundImage: `url(${bgImage})` }}>
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/70"></div>
            </div>
            
            {/* Profile Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="flex items-end gap-4">
                <div className="relative">
                  <Avatar className="w-24 h-24 border-4 border-white shadow-xl">
                    {profileData?.photo_url && <AvatarImage src={profileData.photo_url} alt={user?.name} />}
                    <AvatarFallback className="bg-primary text-primary-foreground text-3xl">
                      {user?.name?.charAt(0) || 'S'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-secondary flex items-center justify-center border-2 border-white">
                    {isDealer ? (
                      <Store className="w-4 h-4 text-secondary-foreground" />
                    ) : (
                      <ChefHat className="w-4 h-4 text-secondary-foreground" />
                    )}
                  </div>
                </div>
                <div className="flex-1 pb-2">
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-white">{user?.name}</h1>
                    <Badge className="bg-white/20 text-white border-none">
                      {isDealer ? 'Vendor' : 'Home Cook'}
                    </Badge>
                  </div>
                  {profileData?.code_name && (
                    <p className="text-white/90 font-medium">{profileData.code_name}</p>
                  )}
                  <p className="text-white/80 flex items-center gap-1">
                    <Phone className="w-4 h-4" /> {user?.mobile}
                  </p>
                </div>
                <Button 
                  onClick={() => setEditMode(true)}
                  className="rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30"
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </div>
            </div>
          </div>

          {/* Profile Details Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Basic Info */}
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-4 py-3 border-b">
                <h3 className="font-semibold flex items-center gap-2">
                  <ChefHat className="w-4 h-4 text-primary" />
                  Basic Information
                </h3>
              </div>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Age</span>
                  <span className="font-medium">{profileData?.age || 'Not set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gender</span>
                  <span className="font-medium">{GENDER_LABELS[profileData?.gender] || 'Not set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Community</span>
                  <span className="font-medium text-right">{entityName || 'Not set'}</span>
                </div>
              </CardContent>
            </Card>

            {/* Specialization (Seller only) */}
            {!isDealer && (
              <Card className="overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500/10 to-orange-500/5 px-4 py-3 border-b">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Award className="w-4 h-4 text-orange-500" />
                    Expertise
                  </h3>
                </div>
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Specialization</span>
                    <span className="font-medium">{profileData?.specialization || 'Not set'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Experience</span>
                    <span className="font-medium">{profileData?.cooking_experience || 'Not set'}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Address */}
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-secondary/10 to-secondary/5 px-4 py-3 border-b">
                <h3 className="font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-secondary" />
                  Address
                </h3>
              </div>
              <CardContent className="p-4">
                <p className="text-foreground">{profileData?.address || 'Not set'}</p>
              </CardContent>
            </Card>

            {/* Payment */}
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500/10 to-blue-500/5 px-4 py-3 border-b">
                <h3 className="font-semibold flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-blue-500" />
                  Payment Details
                </h3>
              </div>
              <CardContent className="p-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">UPI Code</span>
                  <span className="font-mono font-medium">{profileData?.upi_code || 'Not set'}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </SellerLayout>
    );
  }

  // Edit Mode
  return (
    <SellerLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={cancelEdit}
              className="p-2 hover:bg-muted rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h1 className="font-heading text-2xl font-bold">Edit Profile</h1>
          </div>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Photo URL */}
              <div className="space-y-2">
                <Label htmlFor="photo_url">Profile Photo URL</Label>
                <Input
                  id="photo_url"
                  placeholder="https://example.com/photo.jpg"
                  value={formData.photo_url}
                  onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                />
                {formData.photo_url && (
                  <div className="flex justify-center">
                    <Avatar className="w-20 h-20">
                      <AvatarImage src={formData.photo_url} alt="Preview" />
                      <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                  </div>
                )}
              </div>

              {/* Code Name (Seller only) */}
              {!isDealer && (
                <div className="space-y-2">
                  <Label htmlFor="code_name">Kitchen Name</Label>
                  <Input
                    id="code_name"
                    placeholder="e.g., Amma's Kitchen, Homely Bites"
                    value={formData.code_name}
                    onChange={(e) => setFormData({ ...formData, code_name: e.target.value })}
                  />
                </div>
              )}

              {/* Age & Gender */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="age">Age</Label>
                  <Input
                    id="age"
                    type="number"
                    placeholder="Enter your age"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    min="1"
                    max="120"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select 
                    value={formData.gender} 
                    onValueChange={(value) => setFormData({ ...formData, gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Specialization & Experience (Seller only) */}
              {!isDealer && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="specialization">Specialization</Label>
                    <Input
                      id="specialization"
                      placeholder="e.g., South Indian, Sweets, Snacks"
                      value={formData.specialization}
                      onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cooking_experience">Cooking Experience</Label>
                    <Input
                      id="cooking_experience"
                      placeholder="e.g., 10 years, Learned from grandmother"
                      value={formData.cooking_experience}
                      onChange={(e) => setFormData({ ...formData, cooking_experience: e.target.value })}
                    />
                  </div>
                </>
              )}

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  placeholder="Enter your flat/tower number and address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={3}
                />
              </div>

              {/* UPI Code */}
              <div className="space-y-2">
                <Label htmlFor="upi_code">UPI Code</Label>
                <Input
                  id="upi_code"
                  placeholder="yourname@upi"
                  value={formData.upi_code}
                  onChange={(e) => setFormData({ ...formData, upi_code: e.target.value })}
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelEdit}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 rounded-full"
                  disabled={saving}
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
}
